import { useState, useEffect } from 'react';
import { CreditCard, Check, Loader, ArrowLeft, Crown, Ticket, Smartphone, Copy, CheckCircle, Clock, X, ZoomIn, LogIn, UserPlus, Gift } from 'lucide-react';
import axios from 'axios';
import PremiumWelcomeModal from './PremiumWelcomeModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentPage({ onBack, onSuccess, user, onUserUpdate, onLogin }) {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null); // {type, value, percentage, validatedCode, applicablePackages}
  const [discountError, setDiscountError] = useState('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [purchasedPackageName, setPurchasedPackageName] = useState('');
  
  // Payment method selection
  const [paymentMethod, setPaymentMethod] = useState(null); // 'card' or 'swish'
  const [swishSubmitted, setSwishSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Lightbox state for QR image
  const [showLightbox, setShowLightbox] = useState(false);
  
  // Swish settings from admin
  const [swishSettings, setSwishSettings] = useState({
    swish_number: '123-377 74 30',
    swish_image: '/swish-qr.png',
    activation_message: 'Ödemeniz onaylandıktan sonra hesabınız en geç 24 saat içinde aktif edilecektir.',
    instructions: [
      'Swish uygulamanızı açın',
      'QR kodu tarayın veya numarayı girin',
      'Tutarı gönderin',
      'Mesaj kısmına email adresinizi yazın'
    ]
  });

  useEffect(() => {
    fetchPackages();
    fetchSwishSettings();
    checkPaymentReturn();
    trackPaymentPageVisit();
  }, []);

  const trackPaymentPageVisit = async () => {
    try {
      await axios.post(`${API}/track/payment-page-visit`, {
        email: user?.email,
        name: user?.name || 'Ziyaretçi'
      }, {
        headers: user?.email ? { 'X-User-Email': user.email } : {}
      });
    } catch (error) {
      console.log('Cart tracking:', error.message);
    }
  };

  const fetchSwishSettings = async () => {
    try {
      const response = await axios.get(`${API}/swish-settings`);
      if (response.data) {
        setSwishSettings(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.log('Using default Swish settings');
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/payment/packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Paketler yüklenemedi:', error);
    }
  };

  // Validate discount code
  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('');
      setAppliedDiscount(null);
      return;
    }
    
    try {
      const response = await axios.post(`${API}/payment/validate-discount`, {
        code: discountCode.trim(),
        package_id: selectedPackage || ''
      });
      
      if (response.data.valid) {
        setAppliedDiscount({
          type: response.data.discount_type || 'percentage',
          value: response.data.discount_value || response.data.discount_percentage || 0,
          percentage: response.data.discount_percentage || 0,
          validatedCode: discountCode.trim(),
          applicablePackages: response.data.applicable_packages || []
        });
        setDiscountError('');
      } else {
        setAppliedDiscount(null);
        setDiscountError(response.data.message || 'Gecersiz indirim kodu');
      }
    } catch (error) {
      setAppliedDiscount(null);
      setDiscountError(error.response?.data?.detail || 'Indirim kodu dogrulanamadi');
    }
  };

  // Calculate discounted amount
  const getDiscountedAmount = (originalAmount) => {
    if (!appliedDiscount) return originalAmount;
    if (appliedDiscount.type === 'fixed') {
      return Math.max(0, Math.round(originalAmount - appliedDiscount.value));
    }
    if (appliedDiscount.percentage > 0) {
      return Math.round(originalAmount * (100 - appliedDiscount.percentage) / 100);
    }
    return originalAmount;
  };

  // Get discount display text
  const getDiscountText = () => {
    if (!appliedDiscount) return '';
    if (appliedDiscount.type === 'fixed') {
      return `${appliedDiscount.value} SEK indirim`;
    }
    return `%${appliedDiscount.percentage} indirim`;
  };

  // Check if discount is applicable to a package
  const isDiscountApplicable = (pkgId) => {
    if (!appliedDiscount) return true;
    if (!appliedDiscount.applicablePackages || appliedDiscount.applicablePackages.length === 0) return true;
    return appliedDiscount.applicablePackages.includes(pkgId);
  };

  const checkPaymentReturn = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    if (attempts >= maxAttempts) {
      alert('Ödeme kontrolü zaman aşımına uğradı. Lütfen sayfayı yenileyin.');
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`, {
        withCredentials: true,
        headers: user?.email ? { 'X-User-Email': user.email } : {}
      });
      
      if (response.data.status === 'paid' || response.data.payment_status === 'paid') {
        window.history.replaceState({}, document.title, window.location.pathname);
        setCheckingStatus(false);
        
        try {
          await axios.post(`${API}/track/payment-complete`, {
            email: user?.email
          }, {
            headers: user?.email ? { 'X-User-Email': user.email } : {}
          });
        } catch (e) {
          console.log('Cart conversion tracking:', e.message);
        }
        
        try {
          const userResponse = await axios.get(`${API}/auth/me`);
          if (onUserUpdate && userResponse.data.user) {
            onUserUpdate({
              ...userResponse.data.user,
              is_premium: true,
              subscription_status: 'active'
            });
          }
        } catch (userError) {
          console.error('User refresh error:', userError);
        }
        
        setPurchasedPackageName(selectedPackage ? packages[selectedPackage]?.name : 'Premium');
        setShowWelcomeModal(true);
        
      } else if (response.data.status === 'expired') {
        setCheckingStatus(false);
        alert('Ödeme oturumu sona erdi. Lütfen tekrar deneyin.');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Ödeme durumu kontrol hatası:', error);
      setCheckingStatus(false);
      
      // If auth error, try to reload and check again
      if (error.response?.status === 401) {
        alert('Oturum süresi dolmuş olabilir. Lütfen tekrar giriş yapın ve ödeme durumunuz kontrol edilecektir.');
      } else if (error.response?.status === 404) {
        // Transaction might not be created yet, retry
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
          return;
        }
        alert('Ödeme işlemi bulunamadı. Lütfen yönetici ile iletişime geçin.');
      } else {
        alert('Ödeme durumu kontrol edilirken bir hata oluştu. Lütfen sayfayı yenileyin.');
      }
    }
  };

  const handleWelcomeClose = () => {
    setShowWelcomeModal(false);
    if (onSuccess) onSuccess();
  };

  // State for login prompt
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [pendingPackageId, setPendingPackageId] = useState(null);
  const [freeActivationLoading, setFreeActivationLoading] = useState(false);

  // Handle free activation when 100% discount
  const handleFreeActivation = async (packageId) => {
    if (!user || !user.email) {
      setPendingPackageId(packageId);
      setShowLoginPrompt(true);
      return;
    }
    
    setFreeActivationLoading(true);
    setSelectedPackage(packageId);
    
    try {
      const response = await axios.post(`${API}/payment/free-activation`, {
        package_id: packageId,
        discount_code: appliedDiscount?.validatedCode,
        user_email: user.email
      }, {
        withCredentials: true,
        headers: { 'X-User-Email': user.email }
      });
      
      if (response.data.success) {
        setPurchasedPackageName(packages[packageId]?.name || 'Premium');
        setShowWelcomeModal(true);
      }
    } catch (error) {
      console.error('Ücretsiz aktivasyon hatası:', error);
      alert('Aktivasyon başarısız: ' + (error.response?.data?.detail || error.message));
    } finally {
      setFreeActivationLoading(false);
    }
  };

  const handleCardPurchase = async (packageId) => {
    // Check if user is logged in
    if (!user || !user.email) {
      setPendingPackageId(packageId);
      setShowLoginPrompt(true);
      return;
    }
    
    setLoading(true);
    setSelectedPackage(packageId);
    try {
      const originUrl = window.location.origin;
      // Get session token from localStorage if available
      const sessionToken = localStorage.getItem('session_token');
      
      const response = await axios.post(`${API}/payment/checkout`, {
        package_id: packageId,
        origin_url: originUrl,
        discount_code: appliedDiscount?.validatedCode || discountCode || undefined,
        user_email: user.email
      }, {
        withCredentials: true,
        headers: { 
          'X-User-Email': user.email,
          ...(sessionToken && { 'Authorization': `Bearer ${sessionToken}` })
        }
      });
      
      // Handle auto free activation (discount brought price below Stripe minimum)
      if (response.data.free_activation) {
        alert(response.data.message || 'Premium aktif edildi!');
        window.location.href = '/dashboard';
        return;
      }
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Ödeme başlatılamadı:', error);
      alert('Ödeme başlatılırken bir hata oluştu: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  // Swish customer info state (for non-logged in users)
  const [swishCustomerInfo, setSwishCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [showSwishForm, setShowSwishForm] = useState(false);

  const handleSwishPayment = async (packageId) => {
    setSelectedPackage(packageId);
    setPaymentMethod('swish');
    // If user is not logged in, show form to collect info
    if (!user || !user.email) {
      setShowSwishForm(true);
    }
  };

  const handleSwishConfirm = async () => {
    if (!selectedPackage) return;
    
    // If not logged in, validate customer info
    if (!user || !user.email) {
      if (!swishCustomerInfo.name || !swishCustomerInfo.email) {
        alert('Lütfen isim ve email adresinizi girin.');
        return;
      }
      // Basic email validation
      if (!swishCustomerInfo.email.includes('@')) {
        alert('Lütfen geçerli bir email adresi girin.');
        return;
      }
    }
    
    setLoading(true);
    try {
      const originalAmount = packages[selectedPackage]?.amount;
      const finalAmount = getDiscountedAmount(originalAmount);
      
      // Notify admin about Swish payment (with discount info)
      await axios.post(`${API}/payment/swish-pending`, {
        package_id: selectedPackage,
        user_email: user?.email || swishCustomerInfo.email,
        user_name: user?.name || swishCustomerInfo.name,
        user_phone: swishCustomerInfo.phone || null,
        amount: finalAmount,
        original_amount: originalAmount,
        currency: packages[selectedPackage]?.currency,
        discount_code: appliedDiscount?.validatedCode || null,
        discount_type: appliedDiscount?.type || 'percentage',
        discount_value: appliedDiscount?.value || 0,
        discount_percentage: appliedDiscount?.percentage || 0
      });
      
      setSwishSubmitted(true);
      setShowSwishForm(false);
    } catch (error) {
      console.error('Swish bildirim hatası:', error);
      // Still show success - we want user to know their payment is being processed
      setSwishSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const copySwishNumber = () => {
    const numberOnly = swishSettings.swish_number.replace(/[^0-9]/g, '');
    navigator.clipboard.writeText(numberOnly);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 flex items-center justify-center">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-12 text-center">
          <Loader className="w-16 h-16 text-white mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-white mb-2">Ödeme Kontrol Ediliyor...</h2>
          <p className="text-white/70">Lütfen bekleyin</p>
        </div>
      </div>
    );
  }

  // Login prompt modal for card payments
  if (showLoginPrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <LogIn className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Giriş Yapmanız Gerekiyor
            </h2>
            <p className="text-gray-600 mb-6">
              Kredi kartı ile ödeme yapabilmek için önce hesabınıza giriş yapmanız veya yeni bir hesap oluşturmanız gerekmektedir.
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  if (onLogin) onLogin();
                }}
                className="w-full bg-red-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-3"
              >
                <LogIn className="w-6 h-6" />
                Giriş Yap / Kayıt Ol
              </button>
              
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  // Switch to Swish payment which doesn't require login
                  if (pendingPackageId) {
                    setSelectedPackage(pendingPackageId);
                    setPaymentMethod('swish');
                  }
                }}
                className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-3"
              >
                <Smartphone className="w-6 h-6" />
                Swish ile Öde (Giriş Gerekmez)
              </button>
              
              <button
                onClick={() => {
                  setShowLoginPrompt(false);
                  setPendingPackageId(null);
                }}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                Geri Dön
              </button>
            </div>
            
            <p className="text-xs text-gray-500 mt-6">
              💡 Swish ile ödeme yapmak için giriş yapmanıza gerek yoktur.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Swish payment success screen - with QR code and number visible
  if (swishSubmitted) {
    // Build proper image URL - Swish images are in /public folder
    const getSwishImageUrl = () => {
      if (!swishSettings.swish_image) return null;
      const img = swishSettings.swish_image;
      // If full URL, use as-is
      if (img.startsWith('http')) return img;
      // If starts with /api/uploads, use backend URL
      if (img.startsWith('/api/uploads/')) return `${BACKEND_URL}${img}`;
      // For public folder images (like /swish-qr-xxx.png), use frontend URL
      // The image is served from React's public folder, so we use window.location.origin
      if (img.startsWith('/')) {
        // Use the current origin for public folder files
        return `${window.location.origin}${img}`;
      }
      return `${BACKEND_URL}/api/uploads/${img}`;
    };
    
    const swishImageUrl = getSwishImageUrl();
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 py-8 px-4">
        <div className="container mx-auto max-w-lg">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 md:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Ödeme Talebiniz Alındı</h2>
              <p className="text-white/70 text-sm">
                Swish ile ödeme yaptıktan sonra hesabınız <strong>en geç 24 saat içinde</strong> aktif edilecektir.
              </p>
            </div>
            
            {/* Warning Box */}
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl p-4 mb-6">
              <p className="text-yellow-200 text-sm font-semibold text-center">
                ⚠️ Henüz ödeme yapmadıysanız, aşağıdaki bilgileri kullanarak ödemenizi tamamlayın!
              </p>
            </div>
            
            {/* QR Code and Swish Number Section */}
            <div className="bg-white rounded-2xl p-4 mb-6">
              {/* QR Image from Admin */}
              {swishImageUrl && (
                <div className="flex justify-center mb-4">
                  <img 
                    src={swishImageUrl} 
                    alt="Swish QR Kod" 
                    className="max-w-full h-auto rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ maxHeight: '300px', objectFit: 'contain' }}
                    onClick={() => setShowLightbox(true)}
                  />
                </div>
              )}
              
              {/* Swish Number */}
              <div className="text-center">
                <p className="text-gray-600 text-sm mb-2">Swish Numarası:</p>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-2xl font-bold text-gray-900">{swishSettings.swish_number}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(swishSettings.swish_number.replace(/[- ]/g, ''));
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Numarayı kopyala"
                  >
                    {copied ? <CheckCircle className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-gray-600" />}
                  </button>
                </div>
                {copied && <p className="text-green-500 text-sm mt-1">Kopyalandı!</p>}
              </div>
            </div>
            
            {/* Email Info */}
            <div className="bg-white/10 rounded-xl p-4 mb-6">
              <p className="text-white/70 text-sm text-center">
                Ödeme onaylandığında email adresinize bildirim gönderilecektir.
              </p>
            </div>
            
            {/* Back Button */}
            <button
              onClick={onBack}
              className="w-full bg-white text-red-600 font-bold px-8 py-3 rounded-xl hover:bg-white/90 transition-all"
            >
              Ana Sayfaya Dön
            </button>
          </div>
        </div>
        
        {/* Lightbox for QR Image */}
        {showLightbox && swishImageUrl && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
          >
            <button 
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
              onClick={() => setShowLightbox(false)}
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={swishImageUrl} 
              alt="Swish QR Kod - Büyük" 
              className="max-w-full max-h-[90vh] object-contain rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }

  // Swish payment screen
  if (paymentMethod === 'swish' && selectedPackage) {
    const pkg = packages[selectedPackage];
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 py-8 px-4">
        <div className="container mx-auto max-w-lg">
          <button
            onClick={() => { setPaymentMethod(null); setSelectedPackage(null); setShowSwishForm(false); }}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri Dön</span>
          </button>
          
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-6 md:p-8">
            {/* Swish Logo */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center bg-[#008CFF] rounded-2xl p-3 mb-4">
                <Smartphone className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Swish ile Öde</h2>
              {appliedDiscount ? (
                <div className="mt-2">
                  <p className="text-white/50 line-through text-sm">{pkg?.amount} {pkg?.currency}</p>
                  <p className="text-green-400 font-bold text-xl">
                    {getDiscountedAmount(pkg?.amount)} {pkg?.currency}
                    <span className="text-green-300 text-sm ml-2">({getDiscountText()})</span>
                  </p>
                </div>
              ) : (
                <p className="text-white/70 mt-2">{pkg?.name} - {pkg?.amount} {pkg?.currency}</p>
              )}
            </div>
            
            {/* Customer Info Form - Only show if not logged in */}
            {(!user || !user.email) && (
              <div className="bg-white rounded-2xl p-4 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-red-600" />
                  Müşteri Bilgileri
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Adınız Soyadınız <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={swishCustomerInfo.name}
                      onChange={(e) => setSwishCustomerInfo({...swishCustomerInfo, name: e.target.value})}
                      placeholder="Örn: Ali Yılmaz"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Adresiniz <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      value={swishCustomerInfo.email}
                      onChange={(e) => setSwishCustomerInfo({...swishCustomerInfo, email: e.target.value})}
                      placeholder="ornek@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefon Numaranız (Opsiyonel)
                    </label>
                    <input
                      type="tel"
                      value={swishCustomerInfo.phone}
                      onChange={(e) => setSwishCustomerInfo({...swishCustomerInfo, phone: e.target.value})}
                      placeholder="07X XXX XX XX"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  * Ödeme onayı için bu bilgiler gereklidir
                </p>
              </div>
            )}
            
            {/* QR Code Image from Settings with Lightbox - No whitespace */}
            <div 
              className="rounded-2xl overflow-hidden mb-4 cursor-pointer relative group hover:shadow-xl transition-all"
              onClick={() => setShowLightbox(true)}
              data-testid="swish-qr-container"
            >
              <img 
                src={`${swishSettings.swish_image}?t=${Date.now()}`}
                alt="Swish QR Code" 
                className="w-full h-auto block"
                style={{ minHeight: '350px', objectFit: 'contain', background: '#fff' }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 flex items-center justify-center transition-all">
                <ZoomIn className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
              </div>
            </div>
            <p className="text-white/60 text-xs text-center mb-4">Büyütmek için resme tıklayın</p>
            
            {/* Swish Number */}
            <div className="bg-white/10 rounded-xl p-4 mb-6">
              <p className="text-white/70 text-sm mb-2 text-center">Swish Numarası:</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-3xl font-bold text-white tracking-wider">{swishSettings.swish_number}</span>
                <button
                  onClick={copySwishNumber}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-all"
                  title="Kopyala"
                >
                  {copied ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <Copy className="w-5 h-5 text-white" />
                  )}
                </button>
              </div>
            </div>
            
            {/* Instructions */}
            <div className="space-y-3 mb-6">
              {swishSettings.instructions.map((instruction, idx) => {
                let displayText = instruction;
                if (instruction.includes('{{amount}}')) {
                  const finalAmount = appliedDiscount ? getDiscountedAmount(pkg?.amount) : pkg?.amount;
                  displayText = instruction.replace('{{amount}}', `${finalAmount} ${pkg?.currency}`);
                }
                return (
                  <div key={idx} className="flex items-start gap-3 text-white/80">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-sm font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-sm">{displayText}</p>
                  </div>
                );
              })}
            </div>
            
            {/* Confirm Button */}
            <button
              onClick={handleSwishConfirm}
              disabled={loading}
              className="w-full bg-[#008CFF] hover:bg-[#0077DD] text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  <span>Ödemeyi Gönderdim</span>
                </>
              )}
            </button>
            
            <p className="text-white/50 text-xs text-center mt-4">
              {swishSettings.activation_message}
            </p>
          </div>
        </div>
        
        {/* Lightbox Modal for QR Image */}
        {showLightbox && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setShowLightbox(false)}
            data-testid="swish-qr-lightbox"
          >
            <button
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2 bg-white/10 rounded-full"
              onClick={() => setShowLightbox(false)}
              data-testid="close-lightbox-btn"
            >
              <X className="w-8 h-8" />
            </button>
            <img 
              src={swishSettings.swish_image} 
              alt="Swish QR Code - Büyük Görünüm" 
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-950 via-red-800 to-red-900 py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Geri Dön</span>
          </button>
          <h1 className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-600 via-red-500 to-white mb-3">
            Premium Üyelik
          </h1>
          <p className="text-xl text-white/90">
            Tüm sorulara sınırsız erişim kazanın!
          </p>
        </div>

        {/* Discount Code Input */}
        <div className="mb-8 max-w-md mx-auto">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
            <label className="block text-white font-semibold mb-2 flex items-center gap-2">
              <Ticket className="w-5 h-5 text-yellow-400" />
              <span>İndirim Kodu (Opsiyonel)</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={discountCode}
                onChange={(e) => {
                  setDiscountCode(e.target.value.toUpperCase());
                  setAppliedDiscount(null);
                  setDiscountError('');
                }}
                placeholder="Örn: PREMIUM25"
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-red-500 uppercase"
              />
              <button
                onClick={validateDiscountCode}
                disabled={!discountCode.trim()}
                className="px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-semibold rounded-xl transition-all disabled:cursor-not-allowed"
              >
                Uygula
              </button>
            </div>
            {appliedDiscount && (
              <p className="text-sm text-green-400 mt-2 flex items-center gap-1">
                <Check className="w-4 h-4" />
                <span>{getDiscountText()} uygulandi! ({appliedDiscount.validatedCode})</span>
              </p>
            )}
            {discountError && (
              <p className="text-sm text-red-400 mt-2 flex items-center gap-1">
                <X className="w-4 h-4" />
                <span>{discountError}</span>
              </p>
            )}
          </div>
        </div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {Object.entries(packages).map(([packageId, pkg]) => {
            const isPopular = packageId === '1_month';
            return (
              <div
                key={packageId}
                className={`relative bg-white/10 backdrop-blur-xl border ${
                  isPopular ? 'border-yellow-400/50 ring-2 ring-yellow-400/30' : 'border-white/20'
                } rounded-3xl p-8 hover:bg-white/20 transition-all duration-300 hover:shadow-2xl`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
                      <Crown className="w-4 h-4" />
                      <span>Popüler</span>
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{pkg.name}</h3>
                  {appliedDiscount && isDiscountApplicable(packageId) ? (
                    <>
                      <div className="text-2xl text-white/50 line-through mb-1">{pkg.amount}</div>
                      <div className="text-5xl font-black text-green-400 mb-1">{getDiscountedAmount(pkg.amount)}</div>
                      <div className="text-green-300 text-sm mb-1">{getDiscountText()}</div>
                    </>
                  ) : appliedDiscount && !isDiscountApplicable(packageId) ? (
                    <>
                      <div className="text-5xl font-black text-white/50 mb-1">{pkg.amount}</div>
                      <div className="text-orange-300 text-xs mb-1">Bu pakette gecerli degil</div>
                    </>
                  ) : (
                    <div className="text-5xl font-black text-white mb-1">{pkg.amount}</div>
                  )}
                  <div className="text-white/70 font-semibold">{pkg.currency}</div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-white/90">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-sm">2,500 soru</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-sm">Sınırsız pratik</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-sm">Sınav modu</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/90">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-sm">İlerleme takibi</span>
                  </div>
                </div>

                {/* Payment Method Buttons */}
                <div className="space-y-3">
                  {appliedDiscount && isDiscountApplicable(packageId) && getDiscountedAmount(pkg.amount) < 3 ? (
                    /* Free Activation Button for discount bringing price below Stripe minimum (3 SEK) */
                    <button
                      onClick={() => handleFreeActivation(packageId)}
                      disabled={freeActivationLoading && selectedPackage === packageId}
                      className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {freeActivationLoading && selectedPackage === packageId ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          <span>Aktifleştiriliyor...</span>
                        </>
                      ) : (
                        <>
                          <Gift className="w-5 h-5" />
                          <span>Ücretsiz Aktifleştir</span>
                        </>
                      )}
                    </button>
                  ) : (
                    /* Normal Payment Buttons */
                    <>
                      <button
                        onClick={() => handleCardPurchase(packageId)}
                        disabled={loading && selectedPackage === packageId}
                        className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-4 rounded-2xl font-bold hover:shadow-xl transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {loading && selectedPackage === packageId ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            <span>Yükleniyor...</span>
                          </>
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5" />
                            <span>Kart ile Öde</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleSwishPayment(packageId)}
                        className="w-full bg-[#008CFF] hover:bg-[#0077DD] text-white px-6 py-4 rounded-2xl font-bold transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                      >
                        <Smartphone className="w-5 h-5" />
                        <span>Swish ile Öde</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Info */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-6 max-w-2xl mx-auto">
          <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-400" />
            Güvenli Ödeme
          </h3>
          <p className="text-white/70 text-sm mb-4">
            <strong>Kart ödemeleri:</strong> Stripe üzerinden güvenli bir şekilde işlenir ve anında aktif edilir.
          </p>
          <p className="text-white/70 text-sm">
            <strong>Swish ödemeleri:</strong> Manuel olarak kontrol edilir ve en geç 24 saat içinde aktif edilir.
          </p>
        </div>
      </div>
      
      {/* Premium Welcome Modal */}
      <PremiumWelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleWelcomeClose}
        userName={user?.name}
        packageName={purchasedPackageName}
      />
    </div>
  );
}
